import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
 User, Mail, Phone, Calendar, MessageSquare, Eye,
 Edit, Car, UserCircle, DollarSign, Gauge,
Globe, ExternalLink, FileText, History, Loader2, Plus, MessageCircle
} from 'lucide-react'
import { Lead, LeadNote, VehicleInterestDetailed } from '../../types'
import { formatCurrency } from '../../utils/formatters'
import { cn } from '../../utils/cn'
import { TimelineHistory, AutocompleteInput } from '../ui'
import { useLeadHistory } from '../../modules/leads/presentation/hooks'
import { apiService } from '../../services/api'

export type LeadDrawerSalespersonOption = {
  id: string
  label: string
  meta?: string
}

interface LeadDrawerProps {
 lead: Lead | null
 onEdit: () => void
 onClose?: () => void
 useFullPage?: boolean
 onContact?: (leadId: string, channel: 'whatsapp' | 'phone' | 'email', target: string) => Promise<void> | void
 firstFunnelStageId?: string | null
 salespersonOptions?: LeadDrawerSalespersonOption[]
 salespersonOptionsLoading?: boolean
 onAssignSalesperson?: (leadId: string, userId: string) => Promise<void>
}

function effectiveFunnelStageId(lead: Lead, firstStageId: string | null | undefined): string | null {
  if (!firstStageId) return null
  return lead.funnelStageId ?? firstStageId
}

function isLeadInNewFunnelColumn(lead: Lead, firstFunnelStageId: string | null | undefined): boolean {
  if (!firstFunnelStageId) return false
  return effectiveFunnelStageId(lead, firstFunnelStageId) === firstFunnelStageId
}

function primaryWhatsappDigits(lead: Lead): string | null {
  const list = lead.phones && lead.phones.length > 0 ? lead.phones : []
  const preferred = list.find((p) => p.isWhatsApp) ?? list[0]
  const raw = preferred?.phone ?? lead.phone
  if (!raw) return null
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null
  if (digits.startsWith('55')) return digits
  return `55${digits}`
}

const sourceLabel: Record<string, string> = {
 website: 'Site', whatsapp: 'WhatsApp', phone: 'Telefone',
 'walk-in': 'Presencial', email: 'Email', referral: 'Indicação',
 'social-media': 'Rede Social', other: 'Outro'
}

const getSourceIcon = (source: Lead['source']) => {
 switch (source) {
 case 'whatsapp': return <MessageSquare className="h-3.5 w-3.5 text-emerald-500" />
 case 'phone': return <Phone className="h-3.5 w-3.5 text-accent-500" />
 case 'website': return <Eye className="h-3.5 w-3.5 text-purple-500" />
 default: return <User className="h-3.5 w-3.5 text-muted-foreground" />
 }
}

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
 <div className="space-y-2.5">
 <div className="flex items-center gap-2">
 <span className="text-muted-foreground">{icon}</span>
 <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{title}</h4>
 </div>
 {children}
 </div>
)

const InfoBox: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
 <div className={cn('bg-accent/5 dark:bg-primary-800/40 border border-border rounded-lg p-3.5 space-y-2', className)}>
 {children}
 </div>
)

const WhatsAppLogo: React.FC<{ className?: string }> = ({ className }) => (
 <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
 <path d="M12.04 2C6.53 2 2.06 6.47 2.06 11.98c0 1.76.46 3.47 1.33 4.98L2 22l5.18-1.36a9.92 9.92 0 0 0 4.86 1.25h.01c5.5 0 9.97-4.47 9.97-9.98A9.95 9.95 0 0 0 12.04 2Zm5.82 14.24c-.25.7-1.46 1.33-2.02 1.42-.52.08-1.19.12-1.92-.12-.45-.15-1.02-.34-1.76-.66-3.1-1.34-5.12-4.47-5.28-4.68-.16-.21-1.26-1.67-1.26-3.19 0-1.52.8-2.27 1.08-2.58.28-.31.61-.39.81-.39.2 0 .4 0 .57.01.18.01.43-.07.67.51.25.61.84 2.12.92 2.28.08.16.13.35.02.56-.1.21-.16.35-.32.54-.16.19-.34.43-.49.58-.16.16-.32.33-.14.65.18.31.8 1.31 1.73 2.12 1.19 1.04 2.2 1.36 2.51 1.52.31.16.49.14.67-.08.19-.22.79-.92 1-1.24.21-.31.42-.26.71-.16.29.1 1.83.86 2.14 1.01.31.16.52.23.59.36.07.13.07.77-.18 1.47Z" />
 </svg>
)

export const LeadDrawer: React.FC<LeadDrawerProps> = ({
  lead,
  onEdit,
  useFullPage = false,
  onContact,
  firstFunnelStageId = null,
  salespersonOptions = [],
  salespersonOptionsLoading = false,
  onAssignSalesperson,
}) => {
 const { t } = useTranslation()
 const navigate = useNavigate()
 const { history, isLoading: historyLoading, error: historyError } = useLeadHistory(lead?.id)
 const [localLead, setLocalLead] = useState<Lead | null>(lead)
 const [leadNotes, setLeadNotes] = useState<LeadNote[]>(lead?.leadNotes || [])
 const [noteDraft, setNoteDraft] = useState('')
 const [isSavingNote, setIsSavingNote] = useState(false)
 const [isLoadingNotes, setIsLoadingNotes] = useState(false)
 const [statusDraft, setStatusDraft] = useState('')
 const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
 const [assigneeDraftId, setAssigneeDraftId] = useState('')
 const [assigneeDraftLabel, setAssigneeDraftLabel] = useState('')
 const [isSavingAssignee, setIsSavingAssignee] = useState(false)

 const inNewStage = localLead ? isLeadInNewFunnelColumn(localLead, firstFunnelStageId) : false
 const showSalespersonPicker = Boolean(inNewStage && onAssignSalesperson)

 useEffect(() => {
  setLocalLead(lead)
  setLeadNotes(lead?.leadNotes || [])
  setStatusDraft(lead?.status || 'new')
  setAssigneeDraftId(lead?.assignedTo ?? '')
  setAssigneeDraftLabel(lead?.assignedUser?.name ?? '')
 }, [lead])

 const handleNavigateConversations = () => {
   if (!localLead) return
   const qs = new URLSearchParams()
   qs.set('leadId', localLead.id)
   const digits = primaryWhatsappDigits(localLead)
   if (digits) qs.set('phone', digits)
   navigate(`/leads/conversations?${qs.toString()}`)
 }

 const handleSaveAssignee = async () => {
   if (!localLead || !onAssignSalesperson) return
   const currentId = localLead.assignedTo ?? ''
   if (assigneeDraftId === currentId) return
   try {
     setIsSavingAssignee(true)
     await onAssignSalesperson(localLead.id, assigneeDraftId)
   } finally {
     setIsSavingAssignee(false)
   }
 }

 const assigneeDirty = Boolean(
   localLead && (assigneeDraftId || '') !== (localLead.assignedTo ?? ''),
 )

 useEffect(() => {
  if (!lead?.id) return
  let cancelled = false
  const run = async () => {
   try {
    setIsLoadingNotes(true)
    const fetched = await apiService.getLeadNotes(lead.id)
    if (!cancelled) {
      setLeadNotes(Array.isArray(fetched) ? fetched : [])
    }
   } catch {
    if (!cancelled) setLeadNotes([])
   } finally {
    if (!cancelled) setIsLoadingNotes(false)
   }
  }
  void run()
  return () => {
   cancelled = true
  }
 }, [lead?.id])

 if (!localLead) return null

 const sanitizePhone = (value: string) => value.replace(/\D/g, '')
 const formatWhatsappPhoneBr = (value: string) => {
   const cleanPhone = sanitizePhone(value)
   if (!cleanPhone) return ''
   if (cleanPhone.startsWith('55')) return cleanPhone
   return `55${cleanPhone}`
 }

 const handleContact = async (channel: 'whatsapp' | 'phone' | 'email', target: string) => {
   if (!target) return

   try {
    await onContact?.(localLead.id, channel, target)
   } catch {}

   if (channel === 'email') {
     window.open(`mailto:${target}`, '_self')
     return
   }

   if (channel === 'whatsapp') {
     const whatsappPhone = formatWhatsappPhoneBr(target)
     window.open(`https://wa.me/${whatsappPhone}`, '_blank', 'noopener,noreferrer')
     return
   }

   const cleanPhone = sanitizePhone(target)
   window.open(`tel:${cleanPhone}`, '_self')
 }

 const handleAddNote = async () => {
  const noteText = noteDraft.trim()
  if (!noteText || !localLead) return

  try {
   setIsSavingNote(true)
   const created = await apiService.addLeadNote(localLead.id, noteText)
   setLeadNotes((prev) => [created, ...prev])
   setNoteDraft('')
  } catch {
  } finally {
   setIsSavingNote(false)
  }
 }

 const handleUpdateStatus = async () => {
  if (!localLead || !statusDraft) return
  try {
   setIsUpdatingStatus(true)
   const updated = await apiService.updateLead(localLead.id, { status: statusDraft })
   setLocalLead(updated as Lead)
  } catch {
  } finally {
   setIsUpdatingStatus(false)
  }
 }

 return (
 <div className="p-5 space-y-6">

 {/* ── Header ── */}
 <div className="flex items-start gap-3 pb-5 border-b border-border">
 <div className="w-12 h-12 rounded-xl bg-accent/10 dark:bg-primary-800 flex items-center justify-center flex-shrink-0">
 <User className="h-6 w-6 text-accent dark:text-accent" />
 </div>
 <div className="flex-1 min-w-0">
 <h3
 className="text-base font-bold text-foreground truncate"
 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
 >
 {localLead.name}
 </h3>
 <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
 {getSourceIcon(localLead.source)}
 <span>{sourceLabel[localLead.source] || localLead.source}</span>
 <span className="opacity-40">·</span>
 <Calendar className="h-3 w-3" />
 <span>{new Date(localLead.createdAt).toLocaleDateString('pt-BR')}</span>
 </div>
<div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
  <select
    value={statusDraft}
    onChange={(e) => setStatusDraft(e.target.value)}
    className="w-full h-9 rounded-md border border-border bg-card px-2 text-sm"
  >
    <option value="new">new</option>
    <option value="contacted">contacted</option>
    <option value="qualified">qualified</option>
    <option value="proposal">proposal</option>
    <option value="negotiation">negotiation</option>
    <option value="converted">converted</option>
    <option value="lost">lost</option>
  </select>
  <button
    type="button"
    onClick={() => void handleUpdateStatus()}
    disabled={isUpdatingStatus}
    className="px-3 h-9 rounded-md border border-border text-sm font-medium hover:bg-muted disabled:opacity-50"
  >
    {isUpdatingStatus ? '...' : 'Aplicar'}
  </button>
</div>
 </div>
 </div>

 {/* ── Edit action ── */}
 <button
 type="button"
 onClick={useFullPage ? () => navigate(`/leads/edit/${localLead.id}`) : onEdit}
 className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-all duration-150 active:scale-[0.98]"
 >
 {useFullPage
 ? <><ExternalLink className="h-4 w-4" /> Editar em tela cheia</>
 : <><Edit className="h-4 w-4" /> Editar</>
 }
 </button>

 <button
 type="button"
 onClick={handleNavigateConversations}
 className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-muted transition-all duration-150 active:scale-[0.98]"
 >
 <MessageCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
 {t('leads.drawer.openWhatsAppConversations')}
 </button>

 {showSalespersonPicker ? (
 <Section icon={<UserCircle className="h-3.5 w-3.5" />} title={t('leads.drawer.assignSalesperson')}>
 <InfoBox className="space-y-2">
 <AutocompleteInput
 value={assigneeDraftLabel}
 selectedId={assigneeDraftId}
 options={salespersonOptions}
 loading={salespersonOptionsLoading}
 placeholder={t('leads.drawer.assignSalespersonPlaceholder')}
 emptyMessage={t('common.noResults')}
 onChange={setAssigneeDraftLabel}
 onSelect={(option) => {
 setAssigneeDraftId(option.id)
 setAssigneeDraftLabel(option.label)
 }}
 />
 <button
 type="button"
 onClick={() => void handleSaveAssignee()}
 disabled={isSavingAssignee || !assigneeDirty}
 className="w-full px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-50"
 >
 {isSavingAssignee ? t('leads.drawer.savingAssignee') : t('leads.drawer.saveAssignee')}
 </button>
 {localLead.assignedUser?.email ? (
 <p className="text-xs text-muted-foreground">{localLead.assignedUser.email}</p>
 ) : null}
 </InfoBox>
 </Section>
 ) : localLead.assignedUser ? (
 <Section icon={<UserCircle className="h-3.5 w-3.5" />} title={t('leads.seller')}>
 <InfoBox>
 <p className="text-sm font-medium text-foreground">{localLead.assignedUser.name}</p>
 <p className="text-xs text-muted-foreground">{localLead.assignedUser.email}</p>
 </InfoBox>
 </Section>
 ) : null}

 {/* ── Contato ── */}
 <Section icon={<Phone className="h-3.5 w-3.5" />} title="Contato">
 <InfoBox>
 {/* Telefones */}
 {localLead.phones && localLead.phones.length > 0
 ? localLead.phones.map((p, i) => (
 <div key={i} className="flex items-center gap-2 text-sm">
 <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
 <span className="text-foreground">{p.phone}</span>
 {p.isPrimary && (
 <span className="text-[0.625rem] bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 px-1.5 py-0.5 rounded font-medium">
 Principal
 </span>
 )}
 {p.isWhatsApp && <MessageSquare className="h-3 w-3 text-emerald-500 flex-shrink-0" />}
<button
 type="button"
 onClick={() => handleContact('whatsapp', p.phone)}
 className="ml-auto p-1.5 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
 title="Entrar em contato via WhatsApp"
 aria-label="Entrar em contato via WhatsApp"
>
 <WhatsAppLogo className="h-3.5 w-3.5" />
</button>
 </div>
 ))
 : localLead.phone
 ? (
 <div className="flex items-center gap-2 text-sm">
 <Phone className="h-3.5 w-3.5 text-muted-foreground" />
 <span className="text-foreground">{localLead.phone}</span>
<button
 type="button"
 onClick={() => handleContact('whatsapp', localLead.phone || '')}
 className="ml-auto p-1.5 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
 title="Entrar em contato via WhatsApp"
 aria-label="Entrar em contato via WhatsApp"
>
 <WhatsAppLogo className="h-3.5 w-3.5" />
</button>
 </div>
 )
 : null
 }

 {/* Emails */}
 {localLead.emails && localLead.emails.length > 0
 ? localLead.emails.map((e, i) => (
 <div key={i} className="flex items-center gap-2 text-sm">
 <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
 <span className="text-foreground truncate">{e.email}</span>
 {e.isPrimary && (
 <span className="text-[0.625rem] bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
 Principal
 </span>
 )}
<button
 type="button"
 onClick={() => handleContact('email', e.email)}
 className="ml-auto p-1.5 rounded-md text-accent hover:text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors"
 title="Entrar em contato por e-mail"
 aria-label="Entrar em contato por e-mail"
>
 <Mail className="h-3.5 w-3.5" />
</button>
 </div>
 ))
 : localLead.email
 ? (
 <div className="flex items-center gap-2 text-sm">
 <Mail className="h-3.5 w-3.5 text-muted-foreground" />
 <span className="text-foreground">{localLead.email}</span>
<button
 type="button"
 onClick={() => handleContact('email', localLead.email || '')}
 className="ml-auto p-1.5 rounded-md text-accent hover:text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors"
 title="Entrar em contato por e-mail"
 aria-label="Entrar em contato por e-mail"
>
 <Mail className="h-3.5 w-3.5" />
</button>
 </div>
 )
 : null
 }
 </InfoBox>
 </Section>

 {/* ── Veículos ── */}
 {localLead.vehicleInterest && localLead.vehicleInterest.length > 0 && (
 <Section icon={<Car className="h-3.5 w-3.5" />} title="Veículos de Interesse">
 <div className="space-y-2.5">
 {localLead.vehicleInterest.map((vi: VehicleInterestDetailed) => (
 <div
 key={vi.id}
 className="border border-border rounded-lg overflow-hidden bg-card"
 >
 <div className="flex items-start gap-3 p-3">
 {vi.vehicle.photos?.[0] && (
 <img
 src={vi.vehicle.photos[0].url}
 alt={vi.vehicle.name}
 className="w-14 h-14 object-cover rounded-md flex-shrink-0"
 />
 )}
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-foreground truncate">{vi.vehicle.name}</p>
 <p className="text-xs text-muted-foreground mt-0.5">
 {vi.vehicle.brand.name} · {vi.vehicle.model.name} · {vi.vehicle.modelYear}
 </p>
 <div className="flex items-center gap-2 mt-1.5">
 <span
 className="text-sm font-semibold text-foreground"
 style={{ fontFamily: '"JetBrains Mono", monospace' }}
 >
 {formatCurrency(parseFloat(vi.vehicle.salePrice))}
 </span>
 <span className={cn(
 'text-xs px-2 py-0.5 rounded-full font-medium',
 vi.vehicle.status === 'in_stock'
 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
 : 'bg-muted text-muted-foreground'
 )}>
 {vi.vehicle.status === 'in_stock' ? 'Em Estoque' : vi.vehicle.status}
 </span>
 </div>
 {vi.notes && (
 <p className="text-xs text-muted-foreground mt-1.5">"{vi.notes}"</p>
 )}
 </div>
 <div className="flex-shrink-0 w-7 h-7 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
 <span className="text-xs font-bold text-accent-700 dark:text-accent-400">{vi.priority}</span>
 </div>
 </div>
 <div className="px-3 pb-3">
 <button
 type="button"
 onClick={() => navigate(`/proposals/new?leadId=${localLead.id}&vehicleId=${vi.vehicleId}`)}
 className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border text-foreground hover:bg-muted transition-all duration-150"
 >
 <FileText className="h-3.5 w-3.5" />
 Fazer Proposta
 </button>
 </div>
 </div>
 ))}
 </div>
 </Section>
 )}

 {/* ── Preferências ── */}
 {localLead.interest && Object.values(localLead.interest).some((v) => v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0)) && (
 <Section icon={<Globe className="h-3.5 w-3.5" />} title="Preferências">
 <InfoBox className="space-y-3">
 {(localLead.interest.minPrice || localLead.interest.maxPrice) && (
 <div>
 <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Faixa de Preço</p>
 <div className="flex items-center gap-1.5 text-sm">
 <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
 <span
 className="text-foreground font-medium"
 style={{ fontFamily: '"JetBrains Mono", monospace' }}
 >
 {localLead.interest.minPrice && formatCurrency(localLead.interest.minPrice)}
 {localLead.interest.minPrice && localLead.interest.maxPrice && ' — '}
 {localLead.interest.maxPrice && formatCurrency(localLead.interest.maxPrice)}
 </span>
 </div>
 </div>
 )}

 {localLead.interest.maxMileage && (
 <div>
 <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Quilometragem máx.</p>
 <div className="flex items-center gap-1.5 text-sm">
 <Gauge className="h-3.5 w-3.5 text-accent-500" />
 <span
 className="text-foreground font-medium"
 style={{ fontFamily: '"JetBrains Mono", monospace' }}
 >
 {localLead.interest.maxMileage.toLocaleString('pt-BR')} km
 </span>
 </div>
 </div>
 )}

 {(localLead.interest.minYear || localLead.interest.maxYear) && (
 <div>
 <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground mb-1">Ano</p>
 <span
 className="text-sm font-medium text-foreground"
 style={{ fontFamily: '"JetBrains Mono", monospace' }}
 >
 {localLead.interest.minYear || '?'} — {localLead.interest.maxYear || '?'}
 </span>
 </div>
 )}

 <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
 {[
 { label: 'Condição', val: localLead.interest.condition },
 { label: 'Transmissão', val: localLead.interest.transmission },
 { label: 'Combustível', val: localLead.interest.fuelType },
 { label: 'Cor', val: localLead.interest.color },
 ].filter((f) => f.val).map((f) => (
 <div key={f.label}>
 <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground">{f.label}</p>
 <p className="text-sm text-foreground capitalize mt-0.5">{f.val}</p>
 </div>
 ))}
 </div>

 {localLead.interest.characteristics && localLead.interest.characteristics.length > 0 && (
 <div className="pt-2 border-t border-border">
 <p className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">Características</p>
 <div className="flex flex-wrap gap-1">
 {localLead.interest.characteristics.slice(0, 6).map((c, i) => (
 <span key={i} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{c}</span>
 ))}
 {localLead.interest.characteristics.length > 6 && (
 <span className="text-xs text-muted-foreground">+{localLead.interest.characteristics.length - 6}</span>
 )}
 </div>
 </div>
 )}
 </InfoBox>
 </Section>
 )}

 {/* ── Mensagem Inicial ── */}
 {localLead.message && (
 <Section icon={<MessageSquare className="h-3.5 w-3.5" />} title="Mensagem Inicial">
 <div className="bg-accent/5 dark:bg-primary-800/40 border border-border rounded-lg p-3.5">
 <p className="text-sm text-foreground leading-relaxed">{localLead.message}</p>
 </div>
 </Section>
 )}

 <Section icon={<FileText className="h-3.5 w-3.5" />} title="Notas">
 <div className="bg-accent/5 dark:bg-primary-800/40 border border-border rounded-lg p-3.5 space-y-3">
 <textarea
 value={noteDraft}
 onChange={(e) => setNoteDraft(e.target.value)}
 rows={3}
 placeholder="Adicionar nova nota..."
 className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground"
 />
 <button
 type="button"
 onClick={() => void handleAddNote()}
 disabled={isSavingNote || !noteDraft.trim()}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
 >
 {isSavingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
 Adicionar nota
 </button>
 {isLoadingNotes ? (
 <div className="flex items-center gap-2 text-sm text-muted-foreground">
 <Loader2 className="h-4 w-4 animate-spin" />
 Carregando notas...
 </div>
 ) : leadNotes.length > 0 ? (
 <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
 {leadNotes.map((entry) => (
 <div key={entry.id} className="rounded-md border border-border bg-card p-2.5 space-y-1">
 <p className="text-[11px] text-muted-foreground">
 {new Date(entry.createdAt).toLocaleString('pt-BR')}
 </p>
 <p className="text-sm text-foreground whitespace-pre-wrap">{entry.content}</p>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-sm text-muted-foreground">Nenhuma nota adicionada.</p>
 )}
 </div>
 </Section>

<Section icon={<History className="h-3.5 w-3.5" />} title="Histórico">
<div className="bg-accent/5 dark:bg-primary-800/40 border border-border rounded-lg p-3.5">
{historyError ? (
<p className="text-sm text-destructive">{historyError}</p>
) : (
<TimelineHistory
entries={history}
loading={historyLoading}
emptyMessage="Nenhuma atividade registrada"
/>
)}
</div>
</Section>
 </div>
 )
}
